import { ColDef } from 'ag-grid-enterprise';
import { Decimal } from 'decimal.js';
import RateOutput from '@/modules/common/components/format-rate/RateOutput.vue';
import OpenLoansTableAggregateActions from '@/modules/open-loans/components/OpenLoansTableAggregateActions.vue';
import { MultipleLoanAction } from '@/modules/open-loans/types/open-loans';
import { alignCenter, alignLeft, alignRight, comparator, component } from './utils';
import { h } from 'vue';
import * as cols from '@/modules/common/components/ag-table/columns/common';
import { AggregatedLoanByCounterpartyItem, AggregatedLoanBySecurityItem } from '@/utils/api/loans';
import { CustomDataType } from '@/modules/common/components/ag-table/columns/common';
export { side } from './common';

type AggregatedLoanItem = AggregatedLoanByCounterpartyItem | AggregatedLoanBySecurityItem;

export function ticker(): ColDef<AggregatedLoanItem> {
  return {
    field: 'ticker',
    colId: 'ticker',
    headerName: 'Ticker',
    ...alignLeft(),
  };
}

export function cusip(): ColDef<AggregatedLoanItem> {
  return {
    field: 'cusip',
    colId: 'cusip',
    headerName: 'CUSIP',
    cellDataType: CustomDataType.CUSIP,
    ...alignLeft(),
  };
}

// @TODO: we should stop using "counterpartyDisplay" and use "counterparty" instead
// because it's tightly coupled with the API response
export function counterpartyDisplay(): ColDef<AggregatedLoanItem> {
  return {
    field: 'counterpartyDisplay',
    colId: 'counterpartyDisplay',
    headerName: 'Counterparty',
    ...alignLeft(),
  };
}

export const loansCount = (): ColDef<AggregatedLoanItem> =>
  cols.quantity({
    field: 'loansCount',
    headerName: '#Contracts',
  });

export const counterpartyCount = (): ColDef<AggregatedLoanItem> =>
  cols.quantity({
    field: 'counterpartyCount',
    headerName: '#Counterparties',
  });

export const securityCount = (): ColDef<AggregatedLoanItem> =>
  cols.quantity({
    field: 'securityCount',
    headerName: '#Securities',
  });

export const totalOpenQty = (): ColDef<AggregatedLoanItem> =>
  cols.quantity({
    field: 'totalOpenQty',
    headerName: 'Total Open',
  });

const avgRateAdapter = component<{
  rate: number | string | Decimal | null;
}>((props) => () => h(RateOutput, { props }));

export function avgRate(): ColDef<AggregatedLoanItem> {
  return {
    field: 'avgRate',
    colId: 'avgRate',
    headerName: 'Avg Rate',
    cellRendererSelector: (params) =>
      avgRateAdapter({
        rate: params.value,
      }),
    comparator: (a, b) => comparator.decimal(a?.avgRate, b?.avgRate),
    cellDataType: 'text',
    ...alignRight(),
  };
}

export const totalValue = (): ColDef<AggregatedLoanItem> =>
  cols.price({
    field: 'totalValue',
    headerName: 'Total Value',
  });

export const totalRecallQty = (): ColDef<AggregatedLoanItem> =>
  cols.quantity({
    field: 'totalRecallQty',
    headerName: 'Total Recalled',
  });

const actionsAdapter = component<{
  item: AggregatedLoanItem;
  openDialog: (action: MultipleLoanAction, item: AggregatedLoanItem) => void;
}>(
  (props) => () =>
    h(OpenLoansTableAggregateActions, {
      props: { item: props.item },
      on: { 'open-dialog': props.openDialog },
    })
);

export function actions({
  openDialog,
}: {
  openDialog: (action: MultipleLoanAction, item: AggregatedLoanItem) => void;
}): ColDef<AggregatedLoanItem> {
  return {
    colId: 'actions',
    headerName: 'Actions',
    sortable: false,
    cellRendererSelector: (params) =>
      actionsAdapter({
        item: params.data!,
        openDialog,
      }),
    pinned: 'right',
    lockVisible: true,
    width: 150,
    maxWidth: 150,
    suppressColumnsToolPanel: true,
    ...alignCenter(),
  };
}
